import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createBrand, getBrandById, updateBrand } from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import {
  Alert, Box, Button, CircularProgress, Stack, TextField, Typography
} from '@mui/material';

function FormBrand({
  isEditMode = false,
  brandId = null,
  embedded = false,
  onSaved,
  onCancel,
}) {
  const params = useParams();
  const navigate = useNavigate();

  const id = brandId ?? params.id;
  const editing = isEditMode || !!brandId;

  const [descricao, setDescricao] = useState('');
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(editing);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!editing) return;
    let cancelado = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await getBrandById(id);
        if (!cancelado) setDescricao(data.descricao || '');
      } catch (err) {
        if (!cancelado) setError(getApiErrorMessage(err, 'Erro ao carregar a marca.'));
      } finally {
        if (!cancelado) setLoading(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [id, editing]);

  const fechar = () => {
    if (onCancel) onCancel();
    else navigate('/brands');
  };

  const descricaoInvalida = !descricao.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (descricaoInvalida) return;

    setSaving(true);
    setError('');
    try {
      const payload = { descricao: descricao.trim() };
      if (editing) await updateBrand(id, { id: Number(id), ...payload });
      else await createBrand(payload);

      if (onSaved) onSaved(editing ? 'Marca atualizada' : 'Marca criada');
      else navigate('/brands');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao salvar a marca.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      sx={{ p: embedded ? 0 : 3, maxWidth: embedded ? 'none' : 600, mx: 'auto' }}
    >
      {!embedded && (
        <Typography variant="h5" fontWeight={900} sx={{ mb: 2 }}>
          {editing ? 'Editar marca' : 'Nova marca'}
        </Typography>
      )}

      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}

        <TextField
          label="Descrição"
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
          onBlur={() => setTouched(true)}
          error={touched && descricaoInvalida}
          helperText={touched && descricaoInvalida ? 'Informe a descrição.' : ' '}
          autoFocus
          fullWidth
          disabled={loading}
          inputProps={{ maxLength: 100 }}
        />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button color="inherit" variant="outlined" onClick={fechar} disabled={saving}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving || loading}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {editing ? 'Salvar' : 'Criar'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

export default FormBrand;
